import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { SporadicRouteFormInput } from '../RouteFormRHF';
import { TruckVisualization } from '../TruckVisualization';
import { SelectLocationRHF } from '../form/SelectLocationRHF';
import { SelectSubcontractorRHF } from '../form/SelectSubcontractorRHF';
import {
  FuelType,
  SporadicRouteInput,
  useCreateSporadicRouteMutation,
  useGetLocationsForLegLazyQuery,
} from '../../generated/graphql';
import { timeOrNull } from '../../lib/date_time';
import { DatePicker } from '@mui/x-date-pickers';

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  headerBold: {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  toggleGroup: {
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 13,
      padding: theme.spacing(0.5, 3),
    },
    '& .Mui-selected': {
      backgroundColor: `${theme.palette.success.main} !important`,
      color: '#fff !important',
    },
  },
  sidebarField: {
    marginBottom: theme.spacing(2),
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: 12,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  legMarker: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  legConnector: {
    width: 2,
    backgroundColor: theme.palette.success.main,
    minHeight: 40,
    marginLeft: 11,
  },
  lcLabel: {
    fontWeight: 700,
    fontSize: 13,
    minWidth: 36,
    flexShrink: 0,
  },
  lcRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

interface AddUnplannedRouteModalProps {
  open: boolean;
  onClose: () => void;
  terminalLocationId: number;
  terminalLocationName: string;
  transportationDate: string;
  onCreated?: () => void;
}

type Direction = 'inbound' | 'outbound';

interface LcFields {
  capacity: number | null;
  registrationNumber: string;
  containerId: string;
  gateNumber: string;
  operationType: string;
  estDock: string;
  estUndock: string;
}

function defaultLcFields(): LcFields {
  return {
    capacity: null,
    registrationNumber: '',
    containerId: '',
    gateNumber: '',
    operationType: 'Unload',
    estDock: '',
    estUndock: '',
  };
}

function generateRouteId(): string {
  return `Unplanned Route ${Date.now()}`;
}

function getCurrentTimeHHMM(): string {
  const now = new Date();
  return format(now, 'HH:mm');
}

/**
 * Auto-formats a raw keystroke sequence into HH:mm.
 * Strips non-digits, inserts ':' after the 2nd digit automatically.
 * Handles backspace naturally via the resulting string.
 */
/**
 * Handles time input changes with auto-colon insertion.
 * Auto-inserts ':' only when the user just typed the 3rd digit from scratch.
 * Otherwise passes the value through unchanged so existing values can be freely edited.
 */
function handleTimeChange(prev: string, next: string): string {
  // Strip non-digit non-colon characters; keep colon in position
  const digitsOnly = next.replace(/[^\d:]/g, '');
  // Auto-insert colon after 2nd digit when forward-typing (no colon yet)
  if (!digitsOnly.includes(':') && digitsOnly.length === 3) {
    return `${digitsOnly.slice(0, 2)}:${digitsOnly.slice(2)}`;
  }
  return digitsOnly;
}

/** Returns true if value is empty OR is a valid HH:mm with hour 0-23 and minute 0-59 */
function isValidTime(value: string): boolean {
  if (!value || value.trim() === '') return true;
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return false;
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

const isEtdBeforeEta = (eta: string, etd: string): boolean => {
  if (!eta || !etd || !isValidTime(eta) || !isValidTime(etd)) {
    return false;
  }

  const [etaHour, etaMinute] = eta.split(':').map(Number);
  const [etdHour, etdMinute] = etd.split(':').map(Number);

  return etdHour * 60 + etdMinute < etaHour * 60 + etaMinute;
};

export function AddUnplannedRouteModal({
  open,
  onClose,
  terminalLocationId,
  terminalLocationName,
  transportationDate,
  onCreated,
}: AddUnplannedRouteModalProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [createSporadicRoute, { loading: submitting }] =
    useCreateSporadicRouteMutation();

  // Unknown location fetched from backend
  interface UnknownLocationData {
    id: number;
    name: string;
    address: string;
    postalCode: string;
    country: string;
    city: string;
  }
  const [unknownLocation, setUnknownLocation] =
    useState<UnknownLocationData | null>(null);
  const [fetchLocationsForUnknown, { data: unknownSearchData }] =
    useGetLocationsForLegLazyQuery({ fetchPolicy: 'cache-first' });

  // Fetch the real "UNKNOWN LOCATION" from backend when modal opens
  useEffect(() => {
    if (open) {
      fetchLocationsForUnknown({ variables: { locationsSearch: 'unknown' } });
    }
  }, [open]);

  // When search results arrive, find and store the UNKNOWN LOCATION
  useEffect(() => {
    if (unknownSearchData?.locations) {
      const found = unknownSearchData.locations.find(
        (l) => l.name?.toUpperCase() === 'UNKNOWN LOCATION',
      );
      if (found) {
        setUnknownLocation(found as UnknownLocationData);
      }
    }
  }, [unknownSearchData]);

  // When unknownLocation is resolved, set it in the form if the other location is still "unknown"
  useEffect(() => {
    if (unknownLocation && otherLocationIsUnknown) {
      setValue('legs.1.locationId', unknownLocation.id as any);
      setValue('legs.1.location', unknownLocation as any);
    }
  }, [unknownLocation]);

  // Direction state
  const [direction, setDirection] = useState<Direction>('inbound');

  // LC selection
  const [selectedLCs, setSelectedLCs] = useState({
    lc1: true,
    lc2: true,
    lc3: false,
  });

  // LC field data per leg (only the "current location" leg has LC fields)
  const [lc1Fields, setLc1Fields] = useState<LcFields>(defaultLcFields());
  const [lc2Fields, setLc2Fields] = useState<LcFields>(defaultLcFields());
  // ETA / ETD for the current location leg
  const [eta, setEta] = useState<string>(getCurrentTimeHHMM());
  const [etd, setEtd] = useState<string>('');

  // Driver / Subcontractor / Note — managed via RHF form
  const [driverName, setDriverName] = useState<string>('');
  const [driverContact, setDriverContact] = useState<string>('');
  const [note, setNote] = useState<string>('');

  // ETA / ETD for the other location leg
  const [otherEta, setOtherEta] = useState<string>(getCurrentTimeHHMM());
  const [otherEtd, setOtherEtd] = useState<string>('');

  // The "other" location (not the terminal). null means "Unknown" selected.
  const [otherLocationIsUnknown, setOtherLocationIsUnknown] = useState(true);
  const [otherLocationChanged, setOtherLocationChanged] = useState(false);

  const [currentLocationType, setCurrentLocationType] = useState<
    'Load' | 'Unload'
  >('Unload');

  const [otherLocationType, setOtherLocationType] = useState<'Load' | 'Unload'>(
    'Load',
  );

  // Route ID — editable by user
  const [routeId, setRouteId] = useState<string>(generateRouteId);

  // Format transportation date for display
  const dateStr = transportationDate
    ? format(new Date(transportationDate), 'yyyy-MM-dd')
    : format(new Date(), 'yyyy-MM-dd');

  //Date picker for  from location and current location
  const [fromLocationDate, setFromLocationDate] = useState<Dayjs | null>(
    dayjs().subtract(1, 'day'),
  );
  const [currentLocationDate, setCurrentLocationDate] = useState<Dayjs | null>(
    dayjs(),
  );
  // RHF form — we use SporadicRouteFormInput to keep SelectLocationRHF / SelectSubcontractorRHF compatible
  const formMethods = useForm<SporadicRouteFormInput>({
    defaultValues: {
      transportationDate: dateStr,
      routeId: routeId,
      note: '',
      externalNote: '',
      driverName: '',
      driverPhoneNumber: '',
      licensePlate: '',
      currency: 'dkk',
      agreedPrice: 0,
      subcontractorLocationId: undefined,
      dispatcherId: undefined,
      isCopyDriverPlateInfo: false,
      routeDriverName: '',
      routeDriverPhoneNumber: '',
      routeCarRegNumber: '',
      routeTrailerRegNumber: '',
      isCreatedFromPlanned: false,
      isCreatedFromTerminal: true,
      totalBringCost: 0,
      newAgreedPrice: 0,
      finalEveryCost: 0,
      finalAdditionalCost: 0,
      files: [],
      ftl: undefined,
      invoiceNumber: undefined,
      invoiceNote: undefined,
      legs: [
        {
          key: 'leg-0',
          position: 0,
          load: direction === 'outbound',
          unload: direction === 'inbound',
          fuel: FuelType.Diesel,
          arrivalTime: getCurrentTimeHHMM(),
          departureTime: '',
          transportationDateOffset: 0,
          transportationDate: dateStr,
          productionDate: dateStr,
          locationId: terminalLocationId,
          location: {
            id: terminalLocationId,
            name: terminalLocationName,
          },
          loadingListItems: [],
          isNewLegVariant: true,
          isLoadingInCar: true,
          isLoadingInTrailer: true,
          carDetails: {
            loadingListItems: [],
            actualCapacity: undefined,
            registrationNumber: '',
            gateNumber: '',
          },
          trailerDetails: {
            loadingListItems: [],
            actualCapacity: undefined,
            registrationNumber: '',
            gateNumber: '',
          },
        },
        {
          key: 'leg-1',
          position: 1,
          load: direction === 'inbound',
          unload: direction === 'outbound',
          fuel: FuelType.Diesel,
          arrivalTime: '',
          departureTime: '',
          transportationDateOffset: 0,
          transportationDate: dateStr,
          productionDate: dateStr,
          locationId: undefined,
          location: undefined,
          loadingListItems: [],
          isNewLegVariant: true,
          isLoadingInCar: false,
          isLoadingInTrailer: false,
          carDetails: {
            loadingListItems: [],
          },
          trailerDetails: {
            loadingListItems: [],
          },
        },
      ],
    },
  });

  const { setValue, getValues, reset, watch } = formMethods;

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      const newDateStr = transportationDate
        ? format(new Date(transportationDate), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd');

      const currentDate = dayjs();
      setCurrentLocationDate(currentDate);
      setFromLocationDate(currentDate.subtract(1, 'day'));

      const newRouteId = generateRouteId();
      reset({
        transportationDate: newDateStr,
        routeId: newRouteId,
        note: '',
        externalNote: '',
        driverName: '',
        driverPhoneNumber: '',
        licensePlate: '',
        currency: 'dkk',
        agreedPrice: 0,
        subcontractorLocationId: undefined,
        dispatcherId: undefined,
        isCopyDriverPlateInfo: false,
        routeDriverName: '',
        routeDriverPhoneNumber: '',
        routeCarRegNumber: '',
        routeTrailerRegNumber: '',
        isCreatedFromPlanned: false,
        isCreatedFromTerminal: true,
        totalBringCost: 0,
        newAgreedPrice: 0,
        finalEveryCost: 0,
        finalAdditionalCost: 0,
        files: [],
        ftl: undefined,
        invoiceNumber: undefined,
        invoiceNote: undefined,
        legs: [
          {
            key: 'leg-0',
            position: 0,
            load: false,
            unload: true,
            fuel: FuelType.Diesel,
            arrivalTime: getCurrentTimeHHMM(),
            departureTime: '',
            transportationDateOffset: 0,
            transportationDate: newDateStr,
            productionDate: newDateStr,
            locationId: terminalLocationId,
            location: {
              id: terminalLocationId,
              name: terminalLocationName,
            },
            loadingListItems: [],
            isNewLegVariant: true,
            isLoadingInCar: true,
            isLoadingInTrailer: true,
            carDetails: {
              loadingListItems: [],
              actualCapacity: undefined,
              registrationNumber: '',
              gateNumber: '',
            },
            trailerDetails: {
              loadingListItems: [],
              actualCapacity: undefined,
              registrationNumber: '',
              gateNumber: '',
            },
          },
          {
            key: 'leg-1',
            position: 1,
            load: true,
            unload: false,
            fuel: FuelType.Diesel,
            arrivalTime: '',
            departureTime: '',
            transportationDateOffset: 0,
            transportationDate: newDateStr,
            productionDate: newDateStr,
            locationId: undefined,
            location: undefined,
            loadingListItems: [],
            isNewLegVariant: true,
            isLoadingInCar: false,
            isLoadingInTrailer: false,
            carDetails: { loadingListItems: [] },
            trailerDetails: { loadingListItems: [] },
          },
        ],
      });
      setDirection('inbound');
      setCurrentLocationType('Unload');
      setOtherLocationType('Load');
      setOtherLocationChanged(false);
      setSelectedLCs({ lc1: true, lc2: true, lc3: false });
      setLc1Fields(defaultLcFields());
      setLc2Fields(defaultLcFields());
      setEta(getCurrentTimeHHMM());
      setEtd('');
      setDriverName('');
      setDriverContact('');
      setNote('');
      setOtherEta(getCurrentTimeHHMM());
      setOtherEtd('');
      setOtherLocationIsUnknown(true);
      setRouteId(generateRouteId());
      // Re-apply unknown location into form after reset if already fetched
      if (unknownLocation) {
        setValue('legs.1.locationId', unknownLocation.id as any);
        setValue('legs.1.location', unknownLocation as any);
      }
    }
  }, [open, transportationDate, terminalLocationId, terminalLocationName]);

  // Update leg load/unload when direction changes
  useEffect(() => {
    const isInbound = direction === 'inbound';
    // Leg 0 = current location
    setValue('legs.0.load', !isInbound);
    setValue('legs.0.unload', isInbound);
    // Leg 1 = other location
    setValue('legs.1.load', isInbound);
    setValue('legs.1.unload', !isInbound);

    // Update LC operation types to match direction
    setLc1Fields((prev) => ({
      ...prev,
      operationType: currentLocationType,
    }));

    setLc2Fields((prev) => ({
      ...prev,
      operationType: currentLocationType,
    }));
  }, [direction, setValue]);

  // Determine if current location is first or second leg
  // In "inbound": Current Location is leg 0 (destination), unknown/from is leg 1 above it
  // In "outbound": Current Location is leg 0 (origin), unknown/to is leg 1 below it
  // But per the mockups: inbound shows "From Location" (unknown) first, "Current Location" second
  // outbound shows "Current Location" first, "To Location" (unknown) second
  const isCurrentLocationFirst = direction === 'outbound';

  const handleDirectionChange = (
    _: React.MouseEvent<HTMLElement>,
    newDirection: Direction | null,
  ) => {
    if (!newDirection) return;

    setDirection(newDirection);
    setOtherLocationChanged(false);
    setOtherLocationIsUnknown(true);

    if (newDirection === 'inbound') {
      setCurrentLocationType('Unload');
      setOtherLocationType('Load');
    } else {
      setCurrentLocationType('Load');
      setOtherLocationType('Unload');
    }

    const currentDate = dayjs();

    setCurrentLocationDate(currentDate);

    setFromLocationDate(
      newDirection === 'inbound'
        ? currentDate.subtract(1, 'day')
        : currentDate.add(1, 'day'),
    );

    setOtherEta(eta);
    setOtherEtd(etd);

    if (unknownLocation) {
      setValue('legs.1.locationId', unknownLocation.id as any);
      setValue('legs.1.location', unknownLocation as any);
    }
  };
  const isBeforeOrSameDay = (
    firstDate: Dayjs | null,
    secondDate: Dayjs | null,
  ): boolean => {
    if (!firstDate || !secondDate) return true;

    return (
      firstDate.startOf('day').valueOf() <= secondDate.startOf('day').valueOf()
    );
  };

  const toTimestamp = (date: Dayjs | null, time: string): number | null => {
    if (!date || !time || !isValidTime(time)) {
      return null;
    }

    const [hours, minutes] = time.split(':').map(Number);

    return date.hour(hours).minute(minutes).second(0).millisecond(0).valueOf();
  };

  const validateRoute = (): string | null => {
    // The two locations cannot have the same type
    if (currentLocationType === otherLocationType) {
      return 'The two locations cannot have the same location type.';
    }
    if (direction === 'inbound') {
      // FROM date cannot be after CURRENT date.
      // Same date is allowed.
      if (!isBeforeOrSameDay(fromLocationDate, currentLocationDate)) {
        return 'Inbound: From Location Trans Date cannot be after the Current Location Trans Date.';
      }
    }

    if (direction === 'outbound') {
      // CURRENT date cannot be after TO date.
      // Same date is allowed.
      if (!isBeforeOrSameDay(currentLocationDate, fromLocationDate)) {
        return 'Outbound: Current Location Trans Date cannot be after the To Location Trans Date.';
      }
    }
    const currentEtaTimestamp = toTimestamp(currentLocationDate, eta);

    const currentEtdTimestamp = etd
      ? toTimestamp(currentLocationDate, etd)
      : null;

    const otherEtaTimestamp = toTimestamp(fromLocationDate, otherEta);

    const otherEtdTimestamp = otherEtd
      ? toTimestamp(fromLocationDate, otherEtd)
      : null;

    if (currentEtaTimestamp === null) {
      return 'Current location ETA is required and must be valid.';
    }

    if (!otherLocationIsUnknown && otherEtaTimestamp === null) {
      return 'Other location ETA is required and must be valid.';
    }

    // Same-location ETA/ETD validation
    if (
      currentEtdTimestamp !== null &&
      currentEtdTimestamp < currentEtaTimestamp
    ) {
      return 'Current location ETD cannot be before ETA.';
    }

    if (
      otherEtdTimestamp !== null &&
      otherEtaTimestamp !== null &&
      otherEtdTimestamp < otherEtaTimestamp
    ) {
      return 'Other location ETD cannot be before ETA.';
    }

    if (otherLocationIsUnknown) {
      return null;
    }

    if (direction === 'inbound') {
      // FROM must be before CURRENT
      const fromEnd = otherEtdTimestamp ?? otherEtaTimestamp;

      if (
        fromEnd !== null &&
        currentEtaTimestamp !== null &&
        fromEnd > currentEtaTimestamp
      ) {
        return 'Inbound: From Location must be before the Current Location.';
      }
    }

    if (direction === 'outbound') {
      // CURRENT must be before TO
      const currentEnd = currentEtdTimestamp ?? currentEtaTimestamp;

      if (
        currentEnd !== null &&
        otherEtaTimestamp !== null &&
        currentEnd > otherEtaTimestamp
      ) {
        return 'Outbound: To Location must be after the Current Location.';
      }
    }

    return null;
  };

  const handleLcToggle = (compartment: 'lc1' | 'lc2' | 'lc3') => {
    if (compartment === 'lc3') return;
    setSelectedLCs((prev) => ({
      ...prev,
      [compartment]: !prev[compartment],
    }));
  };
  const legs = watch('legs');
  const selectedLocation = legs?.[1]?.location;
  const selectedLocationId = legs?.[1]?.locationId;
  const isSameLocation = () => {
    if (otherLocationIsUnknown) {
      return false;
    }

    if (
      selectedLocationId != null &&
      Number(selectedLocationId) === Number(terminalLocationId)
    ) {
      return true;
    }

    const currentName = terminalLocationName?.trim().toLowerCase();
    const selectedName = selectedLocation?.name?.trim().toLowerCase();

    if (!currentName || !selectedName) {
      return false;
    }

    return currentName === selectedName;
  };

  const updateLcField = (
    lc: 'lc1' | 'lc2',
    field: keyof LcFields,
    value: string | number | null,
  ) => {
    const setter = lc === 'lc1' ? setLc1Fields : setLc2Fields;

    setter((prev) => ({
      ...prev,
      [field]: typeof value === 'string' ? value.toUpperCase() : value,
    }));
  };
  const onSubmitHandler = async () => {
    const validationError = validateRoute();

    if (validationError) {
      window.alert(validationError);
      return;
    }

    const formValues = getValues();
    const otherTransportDate = fromLocationDate
      ? fromLocationDate.format('YYYY-MM-DD')
      : '';

    const currentTransportDate = currentLocationDate
      ? currentLocationDate.format('YYYY-MM-DD')
      : '';
    const subcontractorLocationId = formValues.subcontractorLocationId;

    // Build legs
    const currentLocationLeg = {
      load: currentLocationType === 'Load',
      unload: currentLocationType === 'Unload',
      fuel: FuelType.Diesel,
      gateNumber: '',
      carRegistrationNumber: '',
      trailerRegistrationNumber: '',
      routeDriverName: '',
      routeDriverPhoneNumber: '',
      note: '',
      arrivalTime: eta || '00:00',
      departureTime: timeOrNull(etd),
      actualArrivalDate: currentTransportDate,
      actualDepartureDate: currentTransportDate,
      position: isCurrentLocationFirst ? 0 : 1,
      productionDate: currentTransportDate,
      transportationDate: currentTransportDate,
      transportationDateOffset: 0,
      locationId: terminalLocationId,
      isNewLegVariant: true,
      isLoadingInCar: selectedLCs.lc1,
      isLoadingInTrailer: selectedLCs.lc2,
      carDetails: {
        gateNumber: selectedLCs.lc1 ? lc1Fields.gateNumber : '',
        actualCapacity:
          selectedLCs.lc1 && lc1Fields.capacity
            ? Number(lc1Fields.capacity)
            : null,
        loadingListItems: [],
        registrationNumber: selectedLCs.lc1 ? lc1Fields.registrationNumber : '',
        containerId: selectedLCs.lc1 ? lc1Fields.containerId : undefined,
        estDock: selectedLCs.lc1 ? lc1Fields.estDock || undefined : undefined,
        estUndock: selectedLCs.lc1
          ? lc1Fields.estUndock || undefined
          : undefined,
        operationType: selectedLCs.lc1 ? lc1Fields.operationType : undefined,
      },
      trailerDetails: selectedLCs.lc2
        ? {
            gateNumber: lc2Fields.gateNumber,
            actualCapacity: lc2Fields.capacity
              ? Number(lc2Fields.capacity)
              : null,
            loadingListItems: [],
            registrationNumber: lc2Fields.registrationNumber,
            containerId: lc2Fields.containerId || undefined,
            estDock: lc2Fields.estDock || undefined,
            estUndock: lc2Fields.estUndock || undefined,
            operationType: lc2Fields.operationType || undefined,
          }
        : undefined,
      loadingListItems: [],
    };

    // The other leg (unknown or selected location)
    const otherLocationId = formValues.legs[1]?.locationId;
    const otherLeg = {
      load: otherLocationType === 'Load',
      unload: otherLocationType === 'Unload',
      fuel: FuelType.Diesel,
      gateNumber: '',
      carRegistrationNumber: '',
      trailerRegistrationNumber: '',
      routeDriverName: '',
      routeDriverPhoneNumber: '',
      note: '',
      arrivalTime: otherEta || '00:00',
      departureTime: timeOrNull(otherEtd),
      actualArrivalDate: otherTransportDate,
      actualDepartureDate: otherTransportDate,
      position: isCurrentLocationFirst ? 1 : 0,
      productionDate: otherTransportDate,
      transportationDate: otherTransportDate,
      transportationDateOffset: 0,
      locationId: otherLocationId as number,
      isNewLegVariant: true,
      isLoadingInCar: !otherLocationIsUnknown && selectedLCs.lc1,
      isLoadingInTrailer: !otherLocationIsUnknown && selectedLCs.lc2,
      carDetails:
        !otherLocationIsUnknown && selectedLCs.lc1
          ? {
              gateNumber: lc1Fields.gateNumber,
              actualCapacity: lc1Fields.capacity
                ? Number(lc1Fields.capacity)
                : null,
              loadingListItems: [],
              registrationNumber: lc1Fields.registrationNumber,
              containerId: lc1Fields.containerId || undefined,
              estDock: lc1Fields.estDock || undefined,
              estUndock: lc1Fields.estUndock || undefined,
              operationType: lc1Fields.operationType || undefined,
            }
          : {
              gateNumber: '',
              actualCapacity: null,
              loadingListItems: [],
              registrationNumber: '',
            },
      trailerDetails:
        !otherLocationIsUnknown && selectedLCs.lc2
          ? {
              gateNumber: lc2Fields.gateNumber,
              actualCapacity: lc2Fields.capacity
                ? Number(lc2Fields.capacity)
                : null,
              loadingListItems: [],
              registrationNumber: lc2Fields.registrationNumber,
              containerId: lc2Fields.containerId || undefined,
              estDock: lc2Fields.estDock || undefined,
              estUndock: lc2Fields.estUndock || undefined,
              operationType: lc2Fields.operationType || undefined,
            }
          : undefined,
      loadingListItems: [],
    };

    // Order legs by position
    const legs = isCurrentLocationFirst
      ? [currentLocationLeg, otherLeg]
      : [otherLeg, currentLocationLeg];

    const input: SporadicRouteInput = {
      isCreatedFromPlanned: false,
      isCreatedFromTerminal: true,
      isUnplannedRoute: true,
      transportationDate: currentTransportDate,
      agreedPrice: 0,
      currency: 'dkk',
      note: note,
      externalNote: '',
      routeDriverName: '',
      routeDriverPhoneNumber: '',
      routeCarRegNumber: '',
      routeTrailerRegNumber: '',
      isCopyDriverPlateInfo: false,
      driverName: driverName,
      driverPhoneNumber: driverContact,
      licensePlate: '',
      capacity: null,
      routeId: routeId,
      dispatcherId: formValues.dispatcherId as number,
      subcontractorLocationId: (subcontractorLocationId as number) || 0,
      files: [],
      legs,
    };

    try {
      await createSporadicRoute({
        variables: { input, numberOfCopies: 1 },
      });
      onCreated?.();
      onClose();
    } catch (e) {
      console.error('Error creating unplanned route:', e);
    }
  };

  // Render LC row fields
  const renderLcRow = (
    lcKey: 'lc1' | 'lc2',
    lcLabel: string,
    fields: LcFields,
  ) => {
    if (
      (lcKey === 'lc1' && !selectedLCs.lc1) ||
      (lcKey === 'lc2' && !selectedLCs.lc2)
    )
      return null;

    return (
      <Box className={classes.lcRow} key={lcKey}>
        <Typography className={classes.lcLabel}>{lcLabel}</Typography>
        <TextField
          size="small"
          type="number"
          value={fields.capacity ?? ''}
          onChange={(e) =>
            updateLcField(
              lcKey,
              'capacity',
              e.target.value ? Number(e.target.value) : null,
            )
          }
          sx={{ width: 80 }}
        />
        <TextField
          size="small"
          value={fields.registrationNumber}
          onChange={(e) =>
            updateLcField(lcKey, 'registrationNumber', e.target.value)
          }
          sx={{ width: 110 }}
        />
        <TextField
          size="small"
          value={fields.containerId}
          onChange={(e) => updateLcField(lcKey, 'containerId', e.target.value)}
          sx={{ width: 120 }}
        />
        <TextField
          size="small"
          value={fields.gateNumber}
          onChange={(e) => updateLcField(lcKey, 'gateNumber', e.target.value)}
          sx={{ width: 80 }}
        />
        <TextField
          size="small"
          select
          value={fields.operationType}
          onChange={(e) =>
            updateLcField(lcKey, 'operationType', e.target.value)
          }
          sx={{ width: 160 }}
          SelectProps={{ native: true }}
        >
          <option value="Unload">UNLOAD</option>
          <option value="Load">LOAD</option>
        </TextField>
        <TextField
          size="small"
          type="text"
          value={fields.estDock}
          onChange={(e) =>
            updateLcField(
              lcKey,
              'estDock',
              handleTimeChange(fields.estDock, e.target.value),
            )
          }
          error={!isValidTime(fields.estDock)}
          helperText={!isValidTime(fields.estDock) ? 'HH:mm' : undefined}
          sx={{ width: 85 }}
          inputProps={{ placeholder: 'HH:mm', maxLength: 5 }}
        />
        <TextField
          size="small"
          type="text"
          value={fields.estUndock}
          onChange={(e) =>
            updateLcField(
              lcKey,
              'estUndock',
              handleTimeChange(fields.estUndock, e.target.value),
            )
          }
          error={!isValidTime(fields.estUndock)}
          helperText={!isValidTime(fields.estUndock) ? 'HH:mm' : undefined}
          sx={{ width: 85 }}
          inputProps={{ placeholder: 'HH:mm', maxLength: 5 }}
        />
      </Box>
    );
  };

  // Render the "Current Location" section
  const renderCurrentLocationSection = () => (
    <Box>
      <Typography className={classes.sectionLabel}>CURRENT LOCATION</Typography>
      <Box display="flex" alignItems="center" sx={{ mb: 0.5, gap: 1 }}>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ width: 400, flexShrink: 0 }}
        >
          LOCATION NAME
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ width: 100, flexShrink: 0 }}
        >
          LOCATION TYPE
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ width: 150, flexShrink: 0 }}
        >
          TRANS DATE
        </Typography>

        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ width: 83, flexShrink: 0 }}
        >
          ETA
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ width: 83, flexShrink: 0 }}
        >
          ETD
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          size="small"
          sx={{ width: 400, flexShrink: 0 }}
          value={terminalLocationName}
          InputProps={{
            readOnly: true,
            sx: {
              '& input': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            },
          }}
          variant="outlined"
        />
        <TextField
          select
          size="small"
          sx={{ width: 110 }}
          value={currentLocationType}
          onChange={(e) =>
            setCurrentLocationType(e.target.value as 'Load' | 'Unload')
          }
          variant="outlined"
          SelectProps={{ native: true }}
        >
          <option value="Load">LOAD</option>
          <option value="Unload">UNLOAD</option>
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            inputFormat="yyyy-MM-dd"
            value={currentLocationDate}
            minDate={
              direction === 'inbound'
                ? fromLocationDate ?? undefined
                : undefined
            }
            maxDate={
              direction === 'outbound'
                ? fromLocationDate ?? undefined
                : undefined
            }
            onChange={(newValue) => setCurrentLocationDate(newValue)}
            renderInput={(params) => (
              <TextField {...params} size="small" sx={{ width: 150 }} />
            )}
          />
        </LocalizationProvider>
        <TextField
          size="small"
          sx={{ width: 80 }}
          type="text"
          value={eta}
          onChange={(e) => {
            const newEta = handleTimeChange(eta, e.target.value);
            setEta(newEta);

            // update second ETA field
            if (!otherLocationChanged) {
              setOtherEta(newEta);
            }
          }}
          variant="outlined"
          error={!eta || !isValidTime(eta)}
          helperText={
            !eta ? 'Required' : !isValidTime(eta) ? 'HH:mm' : undefined
          }
          inputProps={{ placeholder: 'HH:mm', maxLength: 5 }}
        />
        <TextField
          size="small"
          sx={{ width: 80 }}
          type="text"
          value={etd}
          onChange={(e) => {
            const newEtd = handleTimeChange(etd, e.target.value);
            setEtd(newEtd);

            // update second ETD field
            if (!otherLocationChanged) {
              setOtherEtd(newEtd);
            }
          }}
          variant="outlined"
          error={Boolean(etd && !isValidTime(etd)) || isEtdBeforeEta(eta, etd)}
          helperText={
            !isValidTime(etd)
              ? 'HH:mm'
              : isEtdBeforeEta(eta, etd)
              ? 'ETD cannot be before ETA'
              : undefined
          }
          inputProps={{ placeholder: 'HH:mm', maxLength: 5 }}
        />
      </Box>

      {/* LC rows */}
      <Box mt={1.5} sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 820 }}>
          <Box display="flex" alignItems="center" sx={{ mb: 0.5, gap: 1 }}>
            <Box sx={{ minWidth: 36, flexShrink: 0 }} />
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ width: 80, flexShrink: 0 }}
            >
              CAPACITY
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ width: 110, flexShrink: 0 }}
            >
              REG. NB
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ width: 120, flexShrink: 0 }}
            >
              CONT. NB
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ width: 80, flexShrink: 0 }}
            >
              GATE NB
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ width: 130, flexShrink: 0 }}
            >
              OPERATION TYPE
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ width: 85, flexShrink: 0, ml: 3 }}
            >
              DOCKING
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ width: 85, flexShrink: 0 }}
            >
              UNDOCKING
            </Typography>
          </Box>
          {renderLcRow('lc1', 'LC1', lc1Fields)}
          {renderLcRow('lc2', 'LC2', lc2Fields)}
        </Box>
      </Box>
    </Box>
  );
  // Render the "Other Location" section (From/To)
  const renderOtherLocationSection = () => {
    const label = direction === 'inbound' ? 'FROM LOCATION' : 'TO LOCATION';
    return (
      <Box>
        <Typography className={classes.sectionLabel}>{label}</Typography>
        {/* Same column headers as current location */}
        <Box display="flex" alignItems="center" sx={{ mb: 0.5, gap: 1 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ width: 400, flexShrink: 0 }}
          >
            LOCATION NAME
          </Typography>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ width: 100, flexShrink: 0 }}
          >
            LOCATION TYPE
          </Typography>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ width: 150, flexShrink: 0 }}
          >
            TRANS DATE
          </Typography>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ width: 80, flexShrink: 0 }}
          >
            ETA
          </Typography>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ width: 80, flexShrink: 0 }}
          >
            ETD
          </Typography>
        </Box>
        {/* Single row of fields */}
        <Box display="flex" alignItems="center" gap={1}>
          {otherLocationIsUnknown ? (
            <TextField
              size="small"
              value={
                unknownLocation
                  ? `${unknownLocation.name} | ${
                      unknownLocation.city
                    } | ${unknownLocation.country.toUpperCase()}`
                  : 'Loading...'
              }
              InputProps={{
                readOnly: true,
                sx: {
                  '& input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                },
              }}
              sx={{ width: 400, flexShrink: 0 }}
            />
          ) : (
            <Box
              sx={{
                width: 400,
                flexShrink: 0,
                ...(isSameLocation() && {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'error.main',
                    borderWidth: 2,
                  },
                }),
              }}
            >
              <SelectLocationRHF
                name="legs.1.locationId"
                controllerName="legs.1.locationId"
                locationPath="legs.1.location"
                size="small"
                fullWidth
                shortAddress
              />
              {isSameLocation() && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {direction === 'inbound'
                    ? 'From Location cannot be the same as Current Location.'
                    : 'To Location cannot be the same as Current Location.'}
                </Typography>
              )}
            </Box>
          )}
          <TextField
            select
            size="small"
            value={otherLocationType}
            onChange={(e) => {
              setOtherLocationChanged(true);
              setOtherLocationType(e.target.value as 'Load' | 'Unload');
            }}
            variant="outlined"
            sx={{ width: 100 }}
            SelectProps={{ native: true }}
          >
            <option value="Load">LOAD</option>
            <option value="Unload">UNLOAD</option>
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              inputFormat="yyyy-MM-dd"
              value={fromLocationDate}
              minDate={
                direction === 'outbound'
                  ? currentLocationDate ?? undefined
                  : undefined
              }
              maxDate={
                direction === 'inbound'
                  ? currentLocationDate ?? undefined
                  : undefined
              }
              onChange={(newValue) => {
                setOtherLocationChanged(true);
                setFromLocationDate(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ width: 150 }} />
              )}
            />
          </LocalizationProvider>
          <TextField
            size="small"
            type="text"
            value={otherEta} // now auto-calculated
            variant="outlined"
            disabled={otherLocationIsUnknown}
            onChange={(e) => {
              setOtherLocationChanged(true);
              setOtherEta(handleTimeChange(otherEta, e.target.value));
            }}
            error={Boolean(
              !otherLocationIsUnknown && otherEta && !isValidTime(otherEta),
            )}
            helperText={
              !otherLocationIsUnknown && otherEta && !isValidTime(otherEta)
                ? 'HH:mm'
                : undefined
            }
            placeholder="HH:mm"
            inputProps={{ maxLength: 5 }}
            sx={{ width: 80 }}
          />
          <TextField
            size="small"
            type="text"
            value={otherEtd}
            variant="outlined"
            disabled={otherLocationIsUnknown}
            onChange={(e) => {
              setOtherLocationChanged(true);
              setOtherEtd(handleTimeChange(otherEtd, e.target.value));
            }}
            error={
              !otherLocationIsUnknown &&
              Boolean(otherEtd) &&
              !isValidTime(otherEtd)
            }
            helperText={
              !otherLocationIsUnknown &&
              Boolean(otherEtd) &&
              !isValidTime(otherEtd)
                ? 'HH:mm'
                : undefined
            }
            placeholder="HH:mm"
            inputProps={{ maxLength: 5 }}
            sx={{ width: 80 }}
          />
          {otherLocationIsUnknown ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setOtherLocationIsUnknown(false);
                setOtherLocationChanged(true);
              }}
            >
              Change
            </Button>
          ) : (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setOtherLocationIsUnknown(true);
                setOtherLocationChanged(false);

                setOtherEta(eta);
                setOtherEtd(etd);
                if (unknownLocation) {
                  setValue('legs.1.locationId', unknownLocation.id as any);
                  setValue('legs.1.location', unknownLocation as any);
                } else {
                  setValue('legs.1.locationId', undefined as any);
                  setValue('legs.1.location', undefined as any);
                }
              }}
            >
              Use Unknown
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  // Render right sidebar
  const renderSidebar = () => (
    <Box>
      <Box className={classes.sidebarField}>
        <Typography className={classes.sectionLabel}>
          {t('attributes.driverName').toUpperCase()}
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={driverName}
          onChange={(e) => setDriverName(e.target.value)}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          sx={{
            '& input': {
              textTransform: 'uppercase',
            },
          }}
        />
      </Box>
      <Box className={classes.sidebarField}>
        <Typography className={classes.sectionLabel}>DRIVER CONTACT</Typography>
        <TextField
          size="small"
          fullWidth
          value={driverContact}
          onChange={(e) => setDriverContact(e.target.value)}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          sx={{
            '& input': {
              textTransform: 'uppercase',
            },
          }}
        />
      </Box>
      <Box className={classes.sidebarField}>
        <Typography className={classes.sectionLabel}>
          {t('resource.subcontractor.capitalized').toUpperCase()}
        </Typography>
        <Box
          sx={{
            '& .MuiInputLabel-root': { display: 'none' },
            '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            '& input': {
              textTransform: 'uppercase',
            },
          }}
        >
          <SelectSubcontractorRHF
            name="subcontractorLocationId"
            controllerName="subcontractorLocationId"
          />
        </Box>
      </Box>
      <Box className={classes.sidebarField}>
        <Typography className={classes.sectionLabel}>
          {t('attributes.note').toUpperCase()}
        </Typography>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          width: 'min(1500px, 96vw)',
          maxHeight: '92vh',
        },
      }}
    >
      <FormProvider {...formMethods}>
        {/* Header */}
        <Box className={classes.header}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <TextField
              size="small"
              value={routeId}
              onChange={(e) => setRouteId(e.target.value)}
              inputProps={{
                style: {
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  padding: '4px 8px',
                },
              }}
              sx={{
                minWidth: 240,
                maxWidth: 400,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 1,
                },
              }}
            />
            <Typography
              className={classes.headerTitle}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {/* | {dateStr} |{' '}*/}
              <span className={classes.headerBold}>ADD UNPLANNED ROUTE</span>
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                backgroundColor: 'grey.400',
                color: '#fff',
                '&:hover': { backgroundColor: 'grey.600' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={onSubmitHandler}
              disabled={
                submitting ||
                isSameLocation() ||
                !eta ||
                !isValidTime(eta) ||
                (!otherLocationIsUnknown &&
                  (!otherEta || !isValidTime(otherEta))) ||
                [
                  etd,
                  otherEtd,
                  lc1Fields.estDock,
                  lc1Fields.estUndock,
                  lc2Fields.estDock,
                  lc2Fields.estUndock,
                ].some((v) => !isValidTime(v))
              }
              sx={{
                backgroundColor: 'success.main',
                color: '#fff',
                '&:hover': { backgroundColor: 'success.dark' },
              }}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
          {/* Top section: Toggle + LC checkboxes on left, Truck model on right */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5 }}
          >
            {/* Left: direction toggle + LC checkboxes on one row */}
            <Box display="flex" alignItems="center" gap={3}>
              <ToggleButtonGroup
                value={direction}
                exclusive
                onChange={handleDirectionChange}
                className={classes.toggleGroup}
                size="small"
              >
                <ToggleButton value="inbound">INBOUND</ToggleButton>
                <ToggleButton value="outbound">OUTBOUND</ToggleButton>
              </ToggleButtonGroup>

              <Box display="flex" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedLCs.lc1}
                      onChange={() => handleLcToggle('lc1')}
                      color="success"
                      size="small"
                    />
                  }
                  label="LC1"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedLCs.lc2}
                      onChange={() => handleLcToggle('lc2')}
                      color="success"
                      size="small"
                    />
                  }
                  label="LC2"
                />
                <FormControlLabel
                  control={<Checkbox checked={false} disabled size="small" />}
                  label="LC3"
                />
              </Box>
            </Box>

            {/* Right: truck visualization */}
            <TruckVisualization
              selectedCompartments={selectedLCs}
              onToggle={handleLcToggle}
            />
          </Box>

          <Divider />

          {/* Content area */}
          <Grid container sx={{ p: 2 }}>
            {/* Left column: legs & LC fields */}
            <Grid item xs={9} sx={{ pr: 2 }}>
              <Box display="flex" gap={1.5}>
                {/* Leg markers column */}
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  sx={{ pt: 1 }}
                >
                  <Box className={classes.legMarker}>1</Box>
                  <Box className={classes.legConnector} />
                  <Box className={classes.legMarker}>2</Box>
                </Box>

                {/* Legs content */}
                <Box flex={1}>
                  {direction === 'inbound' ? (
                    <>
                      {/* Inbound: From (unknown) first, Current Location second */}
                      {renderOtherLocationSection()}
                      <Box my={2}>{renderCurrentLocationSection()}</Box>
                    </>
                  ) : (
                    <>
                      {/* Outbound: Current Location first, To (unknown) second */}
                      {renderCurrentLocationSection()}
                      <Box my={2}>{renderOtherLocationSection()}</Box>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Right column: Driver, Subcontractor, Note */}
            <Grid
              item
              xs={3}
              sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}
            >
              {renderSidebar()}
            </Grid>
          </Grid>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
}
